/**
 * PropertyBacklinks — cross-source per-referring-domain view.
 *
 * Anchor: union of Ahrefs + DFS refdomains. Per-vendor columns aligned
 * with disagreement flags so vendor-specific blind spots become
 * visible (an Ahrefs-only domain might be a stale ref the DFS index
 * already lost; a DFS-only domain might be a recent backlink Ahrefs
 * hasn't picked up yet).
 *
 * Headline counts: total per vendor, overlap count, Ahrefs-only,
 * DFS-only, broken-backlink total.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  Link as LinkIcon, Search, ChevronUp, ChevronDown, ExternalLink,
  Check, X,
} from "lucide-react";
import {
  useProperty, usePropertyBacklinks, usePropertyBacklinkLinks,
  type BacklinkDomainRow, type BacklinksLensFilters,
  type BacklinkLinkRow, type BacklinkLinksFilters,
} from "@/lib/queries";
import GenerateReportButton from "@/components/GenerateReportButton";
import { cn } from "@/lib/utils";

type LensView = "by_domain" | "by_link";


export default function PropertyBacklinks() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const [view, setView] = useState<LensView>("by_domain");

  if (property.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!property.data) return <div className="text-sm text-red-600">Property not found.</div>;
  const p = property.data;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">Backlinks</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Cross-source backlink view for <span className="font-mono">{p.domain}</span>.
            Toggle between per-domain (Ahrefs ∪ DFS refdomains) and per-link
            (individual Ahrefs backlinks with Auriel-side lifecycle).
          </p>
        </div>
        <div className="shrink-0">
          <GenerateReportButton propertyId={propertyId} kind="backlinks" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <ViewPill active={view === "by_domain"} onClick={() => setView("by_domain")}>By referring domain</ViewPill>
        <ViewPill active={view === "by_link"}   onClick={() => setView("by_link")}>By individual link</ViewPill>
      </div>

      {view === "by_domain"
        ? <ByDomainView propertyId={propertyId} domain={p.domain} />
        : <ByLinkView   propertyId={propertyId} />}
    </div>
  );
}


function ViewPill({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium",
        active
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground",
      )}>
      {children}
    </button>
  );
}


// ─── Per-domain view (existing) ────────────────────────────────────


function ByDomainView({ propertyId, domain: _domain }: { propertyId: number; domain: string }) {
  const [filters, setFilters] = useState<BacklinksLensFilters>({
    sort: "ah_dr", sort_dir: "desc", limit: 200, offset: 0,
  });
  const bls = usePropertyBacklinks(propertyId, filters);

  const setF = (patch: Partial<BacklinksLensFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  const counts = bls.data?.headline_counts;

  return (
    <div className="space-y-4">

      {bls.data?.sources_present && (
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-muted-foreground">Sources:</span>
          <SrcChip label="Ahrefs"     present={bls.data.sources_present.ahrefs} />
          <SrcChip label="DataForSEO" present={bls.data.sources_present.dfs} />
        </div>
      )}

      {counts && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
            <Headline label="Ahrefs ref domains" value={counts.ahrefs_refdomains} />
            <Headline label="DFS ref domains"    value={counts.dfs_refdomains} />
            <Headline label="Overlap"            value={counts.overlap} />
            <Headline label="Ahrefs only"        value={counts.ahrefs_only} kind="warn" />
            <Headline label="DFS only"           value={counts.dfs_only} kind="warn" />
            <Headline label="Broken backlinks"   value={counts.broken_backlinks} kind={counts.broken_backlinks > 0 ? "warn" : undefined} />
          </div>
          {/* Lifecycle aggregates — only render if substrate is populated */}
          {(counts.gained_last_7d !== undefined || counts.lost_last_7d !== undefined) && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Headline label="Gained last 7d"  value={counts.gained_last_7d ?? 0} kind={(counts.gained_last_7d ?? 0) > 0 ? "ok" : undefined} />
              <Headline label="Gained last 30d" value={counts.gained_last_30d ?? 0} />
              <Headline label="Lost last 7d"    value={counts.lost_last_7d ?? 0} kind={(counts.lost_last_7d ?? 0) > 0 ? "warn" : undefined} />
              <Headline label="Lost last 30d"   value={counts.lost_last_30d ?? 0} />
            </div>
          )}
        </>
      )}

      <Card>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input type="text" placeholder="filter referring-domain by substring…"
                value={filters.search ?? ""}
                onChange={(e) => setF({ search: e.target.value || undefined })}
                className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs" />
            </div>
            <SrcFilterBtn label="Ahrefs only"
              active={!!filters.only_in_ahrefs}
              onClick={() => setF({ only_in_ahrefs: filters.only_in_ahrefs ? undefined : true })} />
            <SrcFilterBtn label="DFS only"
              active={!!filters.only_in_dfs}
              onClick={() => setF({ only_in_dfs: filters.only_in_dfs ? undefined : true })} />
            <SrcFilterBtn label="Disagreement"
              active={!!filters.only_disagreement}
              onClick={() => setF({ only_disagreement: filters.only_disagreement ? undefined : true })} />
            <SrcFilterBtn label="Lost only"
              active={!!filters.only_lost}
              onClick={() => setF({
                only_lost: filters.only_lost ? undefined : true,
                only_active: filters.only_lost ? undefined : false,
              })} />
            <select value={filters.min_dr ?? ""}
              onChange={(e) => setF({ min_dr: e.target.value ? Number(e.target.value) : undefined })}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-xs">
              <option value="">All DR</option>
              <option value="20">DR ≥ 20</option>
              <option value="40">DR ≥ 40</option>
              <option value="60">DR ≥ 60</option>
              <option value="80">DR ≥ 80</option>
            </select>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            {bls.data && <span>{bls.data.items.length.toLocaleString()} of {bls.data.total.toLocaleString()} domains</span>}
            {Object.keys(filters).some((k) => !["sort", "sort_dir", "limit", "offset"].includes(k) && filters[k as keyof BacklinksLensFilters] !== undefined) && (
              <button type="button"
                onClick={() => setFilters({ sort: "ah_dr", sort_dir: "desc", limit: 200, offset: 0 })}
                className="text-primary hover:underline">Clear filters</button>
            )}
          </div>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <SortHeader label="Domain"        col="domain"        filters={filters} setF={setF} className="w-[26%]" />
              <SortHeader label="Sources"       col="domain"        filters={filters} setF={setF} />
              <SortHeader label="Ah DR"         col="ah_dr"         filters={filters} setF={setF} align="right" />
              <SortHeader label="Ah links"      col="ah_links"      filters={filters} setF={setF} align="right" />
              <SortHeader label="Ah first seen" col="ah_dr"         filters={filters} setF={setF} align="right" />
              <SortHeader label="DFS rank"      col="dfs_rank"      filters={filters} setF={setF} align="right" />
              <SortHeader label="DFS backlinks" col="dfs_backlinks" filters={filters} setF={setF} align="right" />
              <SortHeader label="DFS first seen" col="dfs_rank"     filters={filters} setF={setF} align="right" />
              <SortHeader label="DFS lost"      col="dfs_rank"      filters={filters} setF={setF} align="right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {bls.isLoading && <tr><td colSpan={9} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {bls.data?.items.map((d) => <BlRow key={d.domain} d={d} />)}
            {bls.data && bls.data.items.length === 0 && (
              <tr><td colSpan={9} className="px-2 py-4 text-center text-muted-foreground">
                No referring domains match the filters.
              </td></tr>
            )}
          </tbody>
        </table>

        {bls.data && bls.data.total > (filters.limit ?? 200) && (
          <div className="mt-3 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              Page {Math.floor((filters.offset ?? 0) / (filters.limit ?? 200)) + 1} of{" "}
              {Math.ceil(bls.data.total / (filters.limit ?? 200))}
            </span>
            <div className="flex items-center gap-1">
              <button type="button"
                disabled={(filters.offset ?? 0) === 0}
                onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, (f.offset ?? 0) - (f.limit ?? 200)) }))}
                className="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent disabled:opacity-50">Prev</button>
              <button type="button"
                disabled={(filters.offset ?? 0) + (filters.limit ?? 200) >= bls.data.total}
                onClick={() => setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + (f.limit ?? 200) }))}
                className="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}


// ─── Per-link view (new — substrate-backed, migration 0010) ────────


function ByLinkView({ propertyId }: { propertyId: number }) {
  const [filters, setFilters] = useState<BacklinkLinksFilters>({
    sort: "source_dr", sort_dir: "desc", limit: 200, offset: 0,
  });
  const bls = usePropertyBacklinkLinks(propertyId, filters);

  const setF = (patch: Partial<BacklinkLinksFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  const counts = bls.data?.headline_counts;

  return (
    <div className="space-y-4">
      {counts && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Headline label="Active links"    value={counts.active_total} />
            <Headline label="Dofollow"        value={counts.active_dofollow} />
            <Headline label="Lost (total)"    value={counts.lost_total} kind={counts.lost_total > 0 ? "warn" : undefined} />
            <Headline label="Gained 7d"       value={counts.gained_last_7d} kind={counts.gained_last_7d > 0 ? "ok" : undefined} />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Headline label="Gained 30d"      value={counts.gained_last_30d} />
            <Headline label="Lost 7d"         value={counts.lost_last_7d} kind={counts.lost_last_7d > 0 ? "warn" : undefined} />
            <Headline label="Lost 30d"        value={counts.lost_last_30d} />
            <div />
          </div>
        </>
      )}

      <Card>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input type="text" placeholder="filter by source URL / anchor / refdomain…"
              value={filters.search ?? ""}
              onChange={(e) => setF({ search: e.target.value || undefined })}
              className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs" />
          </div>
          <SrcFilterBtn label="Dofollow only"
            active={!!filters.only_dofollow}
            onClick={() => setF({ only_dofollow: filters.only_dofollow ? undefined : true })} />
          <SrcFilterBtn label="Lost only"
            active={!!filters.only_lost}
            onClick={() => setF({
              only_lost: filters.only_lost ? undefined : true,
              only_active: filters.only_lost ? undefined : false,
            })} />
          <select value={filters.min_dr ?? ""}
            onChange={(e) => setF({ min_dr: e.target.value ? Number(e.target.value) : undefined })}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-xs">
            <option value="">All DR</option>
            <option value="20">DR ≥ 20</option>
            <option value="40">DR ≥ 40</option>
            <option value="60">DR ≥ 60</option>
            <option value="80">DR ≥ 80</option>
          </select>
        </div>
        {bls.data && (
          <div className="mt-2 text-xs text-muted-foreground">
            {bls.data.items.length.toLocaleString()} of {bls.data.total.toLocaleString()} links
          </div>
        )}
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Source page</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Anchor</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Target</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">DR</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Type</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Lifecycle</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {bls.isLoading && <tr><td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {bls.data?.items.map((r, i) => <LinkRow key={i} r={r} />)}
            {bls.data && bls.data.items.length === 0 && (
              <tr><td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">
                No backlinks match. {bls.data.total === 0 && "The backlinks substrate is empty — refresh the Ahrefs lens to populate it."}
              </td></tr>
            )}
          </tbody>
        </table>

        {bls.data && bls.data.total > (filters.limit ?? 200) && (
          <div className="mt-3 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              Page {Math.floor((filters.offset ?? 0) / (filters.limit ?? 200)) + 1} of{" "}
              {Math.ceil(bls.data.total / (filters.limit ?? 200))}
            </span>
            <div className="flex items-center gap-1">
              <button type="button"
                disabled={(filters.offset ?? 0) === 0}
                onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, (f.offset ?? 0) - (f.limit ?? 200)) }))}
                className="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent disabled:opacity-50">Prev</button>
              <button type="button"
                disabled={(filters.offset ?? 0) + (filters.limit ?? 200) >= bls.data.total}
                onClick={() => setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + (f.limit ?? 200) }))}
                className="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}


function LinkRow({ r }: { r: BacklinkLinkRow }) {
  return (
    <tr className={cn("hover:bg-accent/40", r.is_lost && "bg-red-500/5")}>
      <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[28%] truncate">
        <a href={r.url_from} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-0.5 hover:underline" title={r.url_from}>
          <span className="truncate">{stripScheme(r.url_from)}</span>
          <ExternalLink className="h-3 w-3 opacity-50" />
        </a>
        {r.refdomain && (
          <div className="text-[10px] text-muted-foreground">{r.refdomain}</div>
        )}
      </td>
      <td className="px-2 py-1.5 align-top max-w-[20%] truncate">{r.anchor || <span className="text-muted-foreground italic">(empty)</span>}</td>
      <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[24%] truncate">
        <a href={r.url_to} target="_blank" rel="noreferrer"
           className="hover:underline" title={r.url_to}>
          {stripScheme(r.url_to)}
        </a>
      </td>
      <td className="px-2 py-1.5 text-right align-top">{r.source_dr ?? "—"}</td>
      <td className="px-2 py-1.5 text-right align-top">
        {r.is_dofollow
          ? <span className="text-emerald-700 dark:text-emerald-400">dofollow</span>
          : <span className="text-muted-foreground">nofollow</span>}
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {r.is_lost
          ? <span className="text-red-700 dark:text-red-400">lost {r.lost_at?.slice(0, 10) ?? ""}</span>
          : <span className="text-muted-foreground">first seen {r.first_seen_at?.slice(0, 10) ?? "—"}</span>}
      </td>
    </tr>
  );
}


function stripScheme(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/^www\./, "");
}


function BlRow({ d }: { d: BacklinkDomainRow }) {
  return (
    <tr className="hover:bg-accent/40">
      <td className="px-2 py-1.5 align-top font-mono text-[11px]">
        <a href={`https://${d.domain}`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-0.5 hover:underline">
          {d.domain}
          <ExternalLink className="h-3 w-3 opacity-50" />
        </a>
      </td>
      <td className="px-2 py-1.5 align-top">
        <div className="flex items-center gap-0.5 text-[10px]">
          <SrcDot label="A" present={d.in_ahrefs} title="Ahrefs" />
          <SrcDot label="D" present={d.in_dfs} title="DataForSEO" />
        </div>
      </td>
      <td className="px-2 py-1.5 text-right align-top">{d.ah_dr ?? "—"}</td>
      <td className="px-2 py-1.5 text-right align-top">{d.ah_links?.toLocaleString() ?? "—"}</td>
      <td className="px-2 py-1.5 text-right align-top text-muted-foreground">{d.ah_first_seen ?? "—"}</td>
      <td className="px-2 py-1.5 text-right align-top">{d.dfs_rank ?? "—"}</td>
      <td className="px-2 py-1.5 text-right align-top">{d.dfs_backlinks?.toLocaleString() ?? "—"}</td>
      <td className="px-2 py-1.5 text-right align-top text-muted-foreground">{d.dfs_first_seen ?? "—"}</td>
      <td className="px-2 py-1.5 text-right align-top">
        {d.dfs_lost_date ? <span className="text-red-600 dark:text-red-400">{d.dfs_lost_date}</span> : "—"}
      </td>
    </tr>
  );
}


function Headline({ label, value, kind }: {
  label: string; value: number;
  kind?: "warn" | "ok";
}) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn(
        "mt-0.5 text-xl font-semibold",
        kind === "warn" && "text-amber-700 dark:text-amber-400",
        kind === "ok"   && "text-emerald-700 dark:text-emerald-400",
      )}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function SrcDot({ label, present, title }: { label: string; present: boolean; title: string }) {
  return (
    <span title={title}
      className={cn(
        "inline-flex h-4 w-4 items-center justify-center rounded font-mono",
        present ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground/40",
      )}>
      {label}
    </span>
  );
}

function SrcChip({ label, present }: { label: string; present: boolean }) {
  return (
    <span className={cn(
      "inline-flex items-center gap-0.5 rounded px-2 py-0.5",
      present ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground line-through",
    )}>
      {present ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      {label}
    </span>
  );
}

function SrcFilterBtn({ label, active, onClick }: {
  label: string; active: boolean; onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn(
        "rounded-md border px-2 py-1.5 text-xs",
        active ? "border-primary bg-primary/10 text-primary" : "border-input bg-background hover:bg-accent",
      )}>{label}</button>
  );
}

function SortHeader({ label, col, filters, setF, align, className }: {
  label: string; col: string;
  filters: BacklinksLensFilters;
  setF: (patch: Partial<BacklinksLensFilters>) => void;
  align?: "right"; className?: string;
}) {
  const active = filters.sort === col;
  const dir = active ? filters.sort_dir : "asc";
  return (
    <th onClick={() => setF({ sort: col, sort_dir: active && dir === "desc" ? "asc" : "desc", offset: 0 })}
      className={cn(
        "cursor-pointer select-none px-2 py-1.5 font-medium text-muted-foreground hover:text-foreground",
        align === "right" && "text-right", className,
      )}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {active && (dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </span>
    </th>
  );
}
